import { useState } from "react";
import { download } from "../../persist/download";
import { ColumnAlign } from "../widgets/ColumnAlign";
import { OrthoToIpaRules } from "./OrthoToIpaRules";
import { fonimRulesToVersionString } from "./versionFiles";
import { PersistOrtho } from "./persistOrtho";
import { RuleSet } from "../../rules/ruleSet";
import { ConversionRules } from "./conversionRules";
import { ButtonStatusType, StatusButton } from "../widgets/StatusButton";
import { CheckBox } from "../widgets/CheckBox";

/** UI for editing, saving, deleting, downloading rules */
export function RulesEditor(props: {
  readonly version: number,
  readonly rules: ConversionRules,
  readonly rulesChanged: () => void,
  readonly ruleListChanged: () => void,
  readonly name: string,
  readonly ruleNameChanged: (name: string) => void,
  readonly single: Record<string, string>, /** key -> ortho symbol */
  readonly multi: Record<string, string[]>, /** key -> multiple ortho symbols */
  readonly width: number,
}) {
  const [lastVersion, setLastVersion] = useState(props.version);
  const [showRules, setShowRules] = useState(false);
  const [saveStatus, setSaveStatus] = useState<ButtonStatusType>('disabled')
  const [lastName, setLastName] = useState<string | null>(null);

  if (lastVersion !== props.version) {
    // rules changed, so show rules area
    setLastVersion(props.version);
    setShowRules(true);
  }
  if (props.name !== lastName) {
    // the outside pushed a new set of rules, so don't offer to save
    setSaveStatus('disabled');
    setLastName(props.name);
  }

  // intercept all events to track the save status
  function onRulesChanged() {
    props.rulesChanged();
    setSaveStatus(props.name === '' ? 'disabled' : 'pending');
  }
  function onRuleNameChanged(name: string) {
    props.ruleNameChanged(name);
    setLastName(name);  // so we can tell when the outside pushes a new set of rules on us
    setSaveStatus(name === '' ? 'disabled' : 'pending');
  }
  function onSave() {
    setSaveStatus('success');
  }
  function onDelete() {
    setSaveStatus(props.name === '' ? 'disabled' : 'pending');
  }
  function onClear() {
    onRuleNameChanged('');
    props.rules.clearRuleSet();
    props.rulesChanged();
  }

  return (<>
    <SaveAndDeleteRules showRules={showRules} setShowRules={setShowRules}
      saveStatus={saveStatus} onSave={onSave} onDelete={onDelete} onClear={onClear}
      ruleSet={props.rules.getRuleSet()} ruleListChanged={props.ruleListChanged}
      name={props.name} ruleNameChanged={onRuleNameChanged} width={props.width} />
    <OrthoToIpaRules show={showRules} rules={props.rules} version={props.version} rulesChanged={onRulesChanged}
      single={props.single} multi={props.multi} width={props.width} />
  </>);
}

/** UI for saving & deleting rules */
function SaveAndDeleteRules(props: {
  readonly showRules: boolean,
  readonly setShowRules: (on: boolean) => void,
  readonly saveStatus: ButtonStatusType,
  readonly onSave: () => void,
  readonly onDelete: () => void,
  readonly onClear: () => void,
  readonly ruleSet: RuleSet,
  readonly ruleListChanged: () => void,
  readonly name: string,
  readonly ruleNameChanged: (name: string) => void,
  readonly width: number,
}) {
  const [persist] = useState(new PersistOrtho());

  function saveRules() {
    if (props.name === '') return;
    persist.saveRules(props.name, props.ruleSet);
    props.ruleListChanged();
    props.onSave();
  }
  function deleteRules() {
    if (props.name === '') return;
    persist.deleteRules(props.name);
    props.ruleListChanged();
    props.onDelete();
  }
  function downloadRules() {
    const data = fonimRulesToVersionString(props.ruleSet);
    if (data.length > 0) {
      const filename = props.name === '' ? 'fonim-rules.txt' : `${props.name}-rules.txt`;
      download(filename, data);
    }
  }
  function clearRules() {
    props.onClear();
  }

  return (<div>
    <ColumnAlign width={props.width}>{[
      <span key={1}>
        <CheckBox checked={props.showRules} onChange={props.setShowRules}>orthography to IPA rules:</CheckBox>
      </span>,
    ]}</ColumnAlign>
    {!props.showRules ? <></>
      : <>
        <ColumnAlign width={props.width - 100}>{[
          <span key={1}>
            <StatusButton status={props.saveStatus} onClick={saveRules}>save</StatusButton>
            <input type="text" size={12} value={props.name} onChange={(e) => props.ruleNameChanged(e.target.value)} />
            <button onClick={deleteRules} disabled={props.name === ''}>delete</button>
          </span>,
          <span key={2}>
            <button onClick={downloadRules}>download</button>
          </span>,
          <span key={3}>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <button onClick={clearRules}>clear rules</button>
          </span>,
        ]}</ColumnAlign>
        <br />
      </>
    }
  </div>)
}
